import React, { useEffect, useMemo } from 'react';
import { Box, Skeleton, Text, VStack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DeviceOption, SiteDeviceOptionSelect } from '../../../common/components/SiteDeviceOptionSelect';
import { DESKTOP_COMPONENT_HEIGHT, MOBILE_COMPONENT_HEIGHT_EXCLUDING_NAV } from '../../../common/constants';
import { useSiteHasSwitchingMeter } from '../../../common/hooks/use-site-has-switching-meter';
import { useViewportType } from '../../../common/hooks/use-viewport-type';
import { selectSite } from '../../site/siteSlice';
import { ProfilePageBase } from '../ProfilePageBase';
import { SaveOptimisationsButton } from './SaveOptimisationsButton';
import { SmartSaveOptionsSelect } from './SmartSaveOptionsSelect';
import { selectSmartSaveDevices } from './smartSaveSlice';
import { useSyncSmartSaveSettingsToRedux } from './use-sync-smartsave-settings-to-redux';
import { useUpdateSmartSaveDevice } from './use-update-smartsave-device';

export const OptimisationSettings = () => {
  const navigate = useNavigate();
  const { site_id: siteId } = useSelector(selectSite);
  const { isLoaded, isFetchingSiteSmartSaveConfig } = useSyncSmartSaveSettingsToRedux();
  const { isDesktopViewport } = useViewportType();
  const componentMinHeight = isDesktopViewport ? DESKTOP_COMPONENT_HEIGHT : MOBILE_COMPONENT_HEIGHT_EXCLUDING_NAV;
  const siteHasSwitchingMeter = useSiteHasSwitchingMeter();
  const smartSaveDevices = useSelector(selectSmartSaveDevices);
  const handleUpdateSmartSaveDevice = useUpdateSmartSaveDevice();

  const deviceOptions = useMemo<DeviceOption[]>(() => {
    return smartSaveDevices.map((smartSaveDevice) => {
      return {
        siteDeviceId: smartSaveDevice.site_device_id,
        isSelected: smartSaveDevice.active,
      };
    });
  }, [smartSaveDevices]);

  useEffect(() => {
    if (!siteHasSwitchingMeter) {
      navigate(`/site/${siteId}/profile`);
    }
  }, [siteHasSwitchingMeter, navigate, siteId]);

  return (
    <ProfilePageBase
      title={'Optimisations'}
      containerProps={{
        height: componentMinHeight,
      }}
    >
      <VStack minH={componentMinHeight} pb={8}>
        <Box>
          <SmartSaveOptionsSelect />
          <Text p={4}>
            When turned on, SmartSave will automatically adjust your energy use to optimise solar power & periods when
            energy prices are low.
          </Text>

          <Skeleton
            isLoaded={isLoaded || !isFetchingSiteSmartSaveConfig}
            minH={isLoaded && !deviceOptions.length ? 'initial' : 120}
          >
            {!!deviceOptions.length && (
              <>
                <Text px={4} fontWeight={500} mt={6}>
                  SELECTED DEVICES TO OPTIMISE:
                </Text>
                <SiteDeviceOptionSelect
                  deviceOptions={deviceOptions}
                  onToggleDeviceSelection={handleUpdateSmartSaveDevice}
                />
              </>
            )}
          </Skeleton>
        </Box>
        <SaveOptimisationsButton />
      </VStack>
    </ProfilePageBase>
  );
};
